import React, { useMemo } from 'react';

import { Select } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { useGetWorkflowSimple } from 'src/hooks';
import type { WorkflowStep } from 'src/workflow';
import {
  getIsWorkflowStepComplete,
  StepElement,
  StepSeparator,
  useTimelineItemStyles,
} from 'src/workflow';

interface StepTimelineEntryProps {
  activeStep: WorkflowStep | null;
  step: WorkflowStep;
  stepNumber: number;
  totalNumSteps: number;
  workflowId: UUID;
  workflowState: Apollo.WorkflowState;
  showProgress?: boolean;
}

export function StepTimelineEntry({
  activeStep,
  showProgress = true,
  step,
  stepNumber,
  totalNumSteps,
  workflowId,
  workflowState,
}: StepTimelineEntryProps): JSX.Element {
  const { workflow } = useGetWorkflowSimple(workflowId);
  const isStepComplete = useMemo(() => getIsWorkflowStepComplete(step), [step]);
  const isActiveStep = step.id === activeStep?.id;
  const showStepActive =
    isActiveStep && showProgress && workflow?.variant === Apollo.WorkflowVariant.Contract;
  const { classes: timelineItemClasses } = useTimelineItemStyles({
    isActiveStep: showStepActive,
    isNumberedSection: totalNumSteps > 1,
    isStepComplete,
  });

  return (
    <div data-cy={Select.workflow.detail.step.container}>
      <div className={timelineItemClasses.timelineContent}>
        {stepNumber === 1 && (
          <StepSeparator
            actionPlacement={Apollo.ActionPlacement.BeforeStep}
            step={step}
            workflowId={workflowId}
          />
        )}
        <StepElement
          key={step.id}
          isActiveGroup={isActiveStep}
          step={step}
          stepNumber={stepNumber}
          workflowId={workflowId}
          workflowState={workflowState}
        />
        <StepSeparator
          actionPlacement={Apollo.ActionPlacement.AfterStep}
          step={step}
          workflowId={workflowId}
        />
      </div>
    </div>
  );
}
